import { useState, useEffect } from "react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import CaseStatsFilters from "./CaseStatsFilters";
import CaseStatsSummary from "./CaseStatsSummary";
import CaseStatsCharts from "./CaseStatsCharts";

const CaseStatsDashboard = () => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    courts: [],
    diadikasies: [],
    antikeimena: [],
  });
  const [filters, setFilters] = useState({
    court: "",
    diadikasia: "",
    antikeimeno: "",
    dateFrom: "",
    dateTo: "",
  });
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  // Load filter options on mount and when court/diadikasia changes
  const fetchFilterOptions = async (court = "", diadikasia = "") => {
    try {
      const params = new URLSearchParams();
      if (court) params.append("court", court);
      if (diadikasia) params.append("diadikasia", diadikasia);

      const response = await axiosPrivate.get(
        `/case-stats/filters?${params.toString()}`
      );
      setFilterOptions(response.data);
    } catch (err) {
      console.error("Error fetching filter options:", err);
      setError("Αποτυχία φόρτωσης φίλτρων");
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Fetch statistics when filters change
  const fetchStatistics = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (filters.court) params.append("court", filters.court);
      if (filters.diadikasia) params.append("diadikasia", filters.diadikasia);
      if (filters.antikeimeno) params.append("antikeimeno", filters.antikeimeno);
      if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters.dateTo) params.append("dateTo", filters.dateTo);

      const response = await axiosPrivate.get(
        `/case-stats/summary?${params.toString()}`
      );
      setStats(response.data);
    } catch (err) {
      console.error("Error fetching statistics:", err);
      setError("Αποτυχία φόρτωσης στατιστικών");
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch on mount - disabled, require user to select filters
  // useEffect(() => {
  //   fetchStatistics();
  // }, []);

  const handleApplyFilters = () => {
    if (filters.court && filters.diadikasia) {
      fetchStatistics();
    }
  };

  const handleResetFilters = () => {
    setFilters({
      court: "",
      diadikasia: "",
      antikeimeno: "",
      dateFrom: "",
      dateTo: "",
    });
    // Reload all filter options without any filters
    fetchFilterOptions("", "");
    // Will trigger fetchStatistics via useEffect
    setTimeout(() => fetchStatistics(), 0);
  };

  return (
    <div className="min-h-screen bg-gray-50 px-2 py-6 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            Στατιστικά Υποθέσεων
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Αναλύστε συγκεντρωτικά δεδομένα υποθέσεων με βάση το δικαστήριο, τη διαδικασία και το αντικείμενο
          </p>
          <p className="mt-1 text-xs text-gray-500">
            * Τα στατιστικά βασίζονται σε όλες τις υποθέσεις της βάσης δεδομένων
          </p>
        </div>

        {/* Filters */}
        <CaseStatsFilters
          filterOptions={filterOptions}
          filters={filters}
          setFilters={setFilters}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
          loading={loading}
          onFilterChange={fetchFilterOptions}
        />

        {/* Error Message */}
        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="mt-6 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-sm text-gray-600">Φόρτωση στατιστικών...</p>
          </div>
        )}

        {/* Require both court and diadikasia to be selected */}
        {!loading && (!filters.court || !filters.diadikasia) && (
          <div className="mt-6 text-center py-12 bg-white rounded-lg border border-gray-200">
            <p className="text-gray-600">
              Παρακαλώ επιλέξτε Δικαστήριο και Διαδικασία για να δείτε τα στατιστικά
            </p>
          </div>
        )}

        {/* Statistics Content */}
        {!loading && stats && filters.court && filters.diadikasia && (
          <div className="space-y-6 mt-6">
            {/* Summary Cards */}
            <CaseStatsSummary summary={stats.summary} />

            {/* Charts */}
            <CaseStatsCharts
              distribution={stats.distribution}
              distributionDepositToPublication={stats.distributionDepositToPublication}
              distributionHearingToPublication={stats.distributionHearingToPublication}
            />
          </div>
        )}

        {/* Empty State */}
        {!loading && stats && stats.summary.totalCases === 0 && filters.court && filters.diadikasia && (
          <div className="mt-6 text-center py-12 bg-white rounded-lg border border-gray-200">
            <p className="text-gray-600">
              Δεν βρέθηκαν υποθέσεις με τα επιλεγμένα φίλτρα
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseStatsDashboard;
